import React, { useState, useEffect, useRef, createRef, memo, useMemo } from "react";
import { Constants, useMeeting, useParticipant, usePubSub, useTranscription, useTranslation } from "@videosdk.live/react-sdk";
import { BottomBar } from "./components/BottomBar";
import { SidebarConatiner } from "../components/sidebar/SidebarContainer";
import MemorizedWebinarParticipantView from "./components/WebinarParticipantView";
import { PresenterView } from "../components/PresenterView";
import { nameTructed, trimSnackBarText } from "../utils/helper";
import WaitingToJoinScreenNew from "../components/screens/WaitingToJoinScreenNew";
import ConfirmBox from "../components/ConfirmBox";
import useIsMobile from "../hooks/useIsMobile";
import useIsTab from "../hooks/useIsTab";
import useWindowSize from "../hooks/useWindowSize";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";
import { useMeetingAppContext } from "../MeetingAppContextDef";
import TopBarWebinar from "./components/TopBarWebinar";
import { sideBarModes } from "../utils/common";
import { useSiteContext } from "../context/SiteContext";
import WaitingForApproval from "../components/screens/WaitingForApproval";
import { useWhiteboard } from "@videosdk.live/react-sdk";
import ElementSection from "../components/meeting/ElementSection";
import CtaSection from "../components/meeting/CtaSection";
import { getFolderElements } from "../services/elementServices";
import VideoSection from "../components/meeting/VideoSection";
import WebsiteSection from "../components/meeting/WebsiteSection";
import WebinerWaiting from "../components/webiner-branding/WebinerWaiting";
import { initGetStreamChat } from "../utils/getStreamClient";
import test_sound from "../sounds/test_sound.wav";
import { useBreakoutRooms } from "../context/BreakoutRoomsContext";
import logger from '../utils/logger';

export function WebinarContainer({
  onMeetingLeave,
  setIsMeetingLeft,
  setCustomVideoStream,
  micOn,
  setMicOn,
  webcamOn,
  setWebcamOn,
  customAudioStream,
  setCustomAudioStream,
  customVideoStream,
}) {
  const [showJoinScreen, setShowJoinScreen] = useState(null);
  const [showWaitingApproval, setShowWaitingApproval] = useState(false);
  const [participantsData, setParticipantsData] = useState([]);
  const {
    isSafari,
    meetingSettings,
    showElementSection,
    setFolderElementList,
    folderElementList,
    setShowElementSection,
    activeElement,
    setActiveElement,
    meetingRole,
    setIsMeetingReconnecting,
  } = useSiteContext();
  const { startWhiteboard, stopWhiteboard, whiteboardUrl } = useWhiteboard();
  const approvalSoundRef = useRef(null);

  const ParticipantMicStream = memo(
    ({ participantId }) => {
      // Individual hook for each participant
      const { micStream, isLocal, stopConsumingMicStreams, consumeMicStreams } = useParticipant(participantId);
      const { isInSameRoom, currentRoom } = useBreakoutRooms();
      const { localParticipant } = useMeeting();
      const audioRef = useRef(null);

      // Check if this participant should have audio based on breakout room
      const shouldHaveAudio = useMemo(() => {
        // If not in any breakout room, allow all audio
        if (!currentRoom) return true;

        // Always allow self
        if (participantId === localParticipant?.id) return true;

        // Check if participant is in the same room
        return isInSameRoom(participantId);
      }, [currentRoom, participantId, isInSameRoom, localParticipant?.id]);

      // Control audio stream consumption for breakout room isolation
      useEffect(() => {
        if (micStream) {
          try {
            if (!shouldHaveAudio) {
              // Stop consuming mic streams if not in same room
              stopConsumingMicStreams();
            } else {
              // Consume mic streams if in same room
              consumeMicStreams();
            }
          } catch (error) {
            logger("error", `Error controlling mic stream for ${participantId}:`, error);
          }
        }
      }, [shouldHaveAudio, micStream, stopConsumingMicStreams, consumeMicStreams, participantId]);

      useEffect(() => {
        if (!micStream) {
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.srcObject = null;
          }
          return;
        }

        // Only play audio if participant is in the same room or not in any room
        if (!shouldHaveAudio) {
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.srcObject = null;
          }
          return;
        }

        // Create audio element only once
        if (!audioRef.current) {
          audioRef.current = new Audio();
          audioRef.current.autoplay = true;
        }

        const mediaStream = new MediaStream();
        mediaStream.addTrack(micStream.track);

        audioRef.current.srcObject = mediaStream;
        audioRef.current.muted = isLocal;

        audioRef.current.play().catch(() => { });

        return () => {
          // Cleanup when micStream or participant changes
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.srcObject = null;
          }
        };
      }, [micStream, isLocal, shouldHaveAudio, participantId]);

      return null;
    },
    [participantsData]
  );

  const {
    useRaisedHandParticipants,
    raisedHandsParticipants,
    setSelectedMic,
    setSelectedWebcam,
    setSelectedSpeaker,
    setUnreadChat,
    sideBarMode,
    setRecentReactions,
    transcriptionEnabled,
    participantApproveFunc,
    setParticipantApproveFunc,
    approvalSoundEnabled,
    setApprovalSoundEnabled,
    participantWebinarFunc,
    setParticipantWebinarFunc,
  } = useMeetingAppContext();

  const bottomBarHeight = 60;
  const [containerHeight, setContainerHeight] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [localParticipantAllowedJoin, setLocalParticipantAllowedJoin] =
    useState(null);
  const [meetingErrorVisible, setMeetingErrorVisible] = useState(false);
  const [meetingError, setMeetingError] = useState(false);
  const [transcriptions, setTranscriptions] = useState([]);
  const [isTranscriptionPanelVisible, setIsTranscriptionPanelVisible] =
    useState(false);
  const PANEL_FIXED_HEIGHT = 180; // Fixed height at 180px
  const PANEL_MOBILE_HEIGHT = 140; // Reduced height for mobile
  const [panelSize, setPanelSize] = useState({
    width: 450,
    height: PANEL_FIXED_HEIGHT,
  });
  const [panelPosition, setPanelPosition] = useState(() => {
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    const panelHeight = PANEL_FIXED_HEIGHT;
    const initialWidth = 450;
    return {
      x: Math.max(0, Math.floor((windowWidth - initialWidth) / 2)),
      y: windowHeight - panelHeight - 20,
    };
  });

  // Panel size constraints
  const PANEL_MIN_WIDTH = 300;
  const PANEL_MAX_WIDTH = Number.MAX_SAFE_INTEGER; // Allow expansion up to right edge
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const transcriptionPanelRef = useRef(null);
  const transcriptionListRef = useRef(null);
  const [isElementRefreshing, setIsElementRefreshing] = useState(false);

  // Whiteboard dragging and resizing states
  const [whiteboardPosition, setWhiteboardPosition] = useState(() => {
    // Position at left and vertically centered
    const windowHeight = window.innerHeight;
    const whiteboardHeight = 600;
    return {
      x: 20, // 20px from left
      y: (windowHeight - whiteboardHeight) / 2, // vertically centered
    };
  });
  const [whiteboardSize, setWhiteboardSize] = useState({
    width: 800,
    height: 600,
  });
  const [isWhiteboardDragging, setIsWhiteboardDragging] = useState(false);
  const [isWhiteboardResizing, setIsWhiteboardResizing] = useState(false);
  const [whiteboardDragOffset, setWhiteboardDragOffset] = useState({
    x: 0,
    y: 0,
  });
  const [whiteboardResizeHandle, setWhiteboardResizeHandle] = useState(null);
  const whiteboardRef = useRef(null);

  const mMeetingRef = useRef();
  const containerRef = createRef();
  const containerHeightRef = useRef();
  const containerWidthRef = useRef();

  useEffect(() => {
    containerHeightRef.current = containerHeight;
    containerWidthRef.current = containerWidth;
  }, [containerHeight, containerWidth]);

  const isMobile = useIsMobile();
  const isTab = useIsTab();
  const windowSize = useWindowSize();
  const isMobileLike =
    (windowSize?.height || 0) > 0 && windowSize.height < 500 ? true : isMobile;
  const isLGDesktop = useMediaQuery({ minWidth: 1024, maxWidth: 1439 });
  const isXLDesktop = useMediaQuery({ minWidth: 1440 });

  const sideBarContainerWidth = isXLDesktop
    ? 400
    : isLGDesktop
      ? 360
      : isTab
        ? 320
        : isMobile
          ? 280
          : 240;

  useEffect(() => {
    if (isMobile) {
      const widthForMobile = Math.min(window.innerWidth - 32, panelSize.width);
      setPanelSize((prev) => ({
        ...prev,
        width: widthForMobile,
        height: PANEL_MOBILE_HEIGHT,
      }));
      setPanelPosition({
        x: (window.innerWidth - widthForMobile) / 2,
        y: window.innerHeight - PANEL_MOBILE_HEIGHT - 20,
      });
    }
  }, [isMobile]);

  useEffect(() => {
    containerRef.current?.offsetHeight &&
      setContainerHeight(containerRef.current.offsetHeight);
    containerRef.current?.offsetWidth &&
      setContainerWidth(containerRef.current.offsetWidth);

    window.addEventListener("resize", ({ target }) => {
      containerRef.current?.offsetHeight &&
        setContainerHeight(containerRef.current.offsetHeight);
      containerRef.current?.offsetWidth &&
        setContainerWidth(containerRef.current.offsetWidth);
    });
  }, [containerRef]);

  const { participantRaisedHand } = useRaisedHandParticipants();

  const _handleMeetingLeft = () => {
    setIsMeetingLeft(true);
  };

  const _handleOnRecordingStateChanged = ({ status }) => {
    if (
      status === Constants.recordingEvents.RECORDING_STARTED ||
      status === Constants.recordingEvents.RECORDING_STOPPED
    ) {
      toast(
        `${status === Constants.recordingEvents.RECORDING_STARTED
          ? "Meeting recording is started"
          : "Meeting recording is stopped."
        }`,
        {
          position: "bottom-left",
          autoClose: 4000,
          hideProgressBar: true,
          closeButton: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        }
      );
    }
  };

  function onParticipantJoined(participant) {
    logger("log", "onParticipantJoined", participant);
    // Change quality to low, med or high based on resolution
    participant && participant.setQuality("high");
  }

  function onEntryRequested(data) {
    const { participantId, name, allow, deny } = data;
    logger("log", "onEntryRequested", data);
    // play audio from assets
    if (meetingSettings && meetingSettings.hostApprovalRequired) {
      const shouldPlaySound =
        ["moderator", "co-host"].includes(meetingRole) &&
        approvalSoundEnabled === true;

      if (shouldPlaySound && approvalSoundRef?.current) {
        approvalSoundRef.current.play().catch((err) => {
          logger("log", "Error playing approval sound:", err);
        });
      }
      setParticipantApproveFunc((prevFuncs) => [
        ...prevFuncs,
        { name, participantId, allow, deny },
      ]);
      setTimeout(() => {
        logger("log", "participantApproveFunc", participantApproveFunc);
      }, 10000);
    } else {
      allow();
    }
  }

  function onEntryResponded(participantId, name) {
    logger("log", "onEntryResponded", participantId, name);
    if (mMeetingRef.current?.localParticipant?.id === participantId) {
      if (name === "allowed") {
        setLocalParticipantAllowedJoin(true);
      } else {
        setLocalParticipantAllowedJoin(false);
        logger("log", "onEntryResponded", name);
        setTimeout(() => {
          _handleMeetingLeft();
        }, 3000);
      }
    }
  }

  function onMeetingJoined() {
    logger("log", "onMeetingJoined");
    if (
      meetingSettings?.muteParticipants &&
      !["moderator", "co-host", "speaker"].includes(meetingRole)
    ) {
      setTimeout(() => {
        if (mMeetingRef.current) {
          mMeetingRef.current.muteMic();
          setMicOn(false);
        }
      }, 100);
    }
    // enableAdaptiveSubscriptions();
  }

  function onMeetingLeft() {
    setSelectedMic({ id: null, label: null });
    setSelectedWebcam({ id: null, label: null });
    setSelectedSpeaker({ id: null, label: null });
    onMeetingLeave();
  }

  const _handleOnError = (data) => {
    const { code, message } = data;
    logger("log", "meetingErr", code, message);

    const joiningErrCodes = [
      4001, 4002, 4003, 4004, 4005, 4006, 4007, 4008, 4009, 4010,
    ];

    const isJoiningError = joiningErrCodes.findIndex((c) => c === code) !== -1;
    const isCriticalError = `${code}`.startsWith("500");

    // new Audio(
    //     isCriticalError
    //         ? `https://static.videosdk.live/prebuilt/notification_critical_err.mp3`
    //         : `https://static.videosdk.live/prebuilt/notification_err.mp3`
    // ).play();

    setMeetingErrorVisible(true);
    setMeetingError({
      code,
      message: isJoiningError ? "Unable to join meeting!" : message,
    });
  };
  const { enableAdaptiveSubscriptions, ...mMeeting } = useMeeting({
    onParticipantJoined,
    onMeetingJoined,
    onMeetingStateChanged: ({ state }) => {
      logger("log", "onMeetingStateChanged", state);
      if (state === "CONNECTING") {
        setShowWaitingApproval(true);
      }

      if (state === "RECONNECTING") {
        logger("log", "Meeting is Reconnecting");
        setIsMeetingReconnecting(true);
      }

      if (state === "CONNECTED") {
        setShowWaitingApproval(false);
        setShowJoinScreen(true);
        setTimeout(() => {
          setShowJoinScreen(false);
        }, 3500);
      }

      if (state === "DISCONNECTED") {
        setIsMeetingReconnecting(false);
      }
    },
    onMeetingLeft,
    onError: _handleOnError,
    onRecordingStateChanged: _handleOnRecordingStateChanged,
    onEntryRequested: onEntryRequested,
    onEntryResponded: onEntryResponded,
  });

  const { isInSameRoom, currentRoom, participantRooms, rooms } = useBreakoutRooms();

  // Filter screen share by breakout room - only show screen share from participants in the same room
  const presenterId = mMeeting.presenterId;
  const isPresenting = presenterId ? true : false;

  // Check if presenter is in the same room (for breakout room isolation)
  const shouldShowScreenShare = useMemo(() => {
    if (!presenterId) return false;
    if (presenterId === mMeeting?.localParticipant?.id) return true; // Always show own screen share

    if (!currentRoom) {
      // If in main meeting, only show screen shares from participants NOT in any breakout room
      return !participantRooms[presenterId];
    }

    // If in a breakout room, only show screen share if presenter is in the same room
    return isInSameRoom(presenterId);
  }, [presenterId, mMeeting?.localParticipant?.id, currentRoom, isInSameRoom, participantRooms]);

  const effectiveIsPresenting = isPresenting && shouldShowScreenShare;

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      const participantIds = Array.from(mMeeting.participants.keys());
      logger("log", "Debounced participantIds", participantIds);
      logger("log", "mMeeting.participants", mMeeting.participants);

      // Filter to only include presenters (SEND_AND_RECV mode) for webinar
      const presenterIds = participantIds.filter((participantId) => {
        const participant = mMeeting.participants.get(participantId);
        return participant?.mode === Constants.modes.SEND_AND_RECV;
      });

      setParticipantsData(presenterIds);
      logger("log", "Setting webinar presenters", presenterIds);
    }, 500);

    return () => clearTimeout(debounceTimeout);
  }, [mMeeting.participants]);

  useEffect(() => {
    mMeetingRef.current = mMeeting;
  }, [mMeeting]);

  useEffect(() => {
    if (
      mMeeting &&
      mMeeting.localParticipant &&
      meetingSettings?.muteParticipants &&
      !meetingSettings?.guestCanUnmute &&
      !["moderator", "co-host", "speaker"].includes(meetingRole) &&
      mMeeting.localParticipant.micOn
    ) {
      mMeeting.muteMic();
      setMicOn(false);
    }
  }, [
    mMeeting,
    meetingSettings?.muteParticipants,
    meetingSettings?.guestCanUnmute,
    meetingRole,
  ]);

  usePubSub("RAISE_HAND", {
    onMessageReceived: (data) => {
      const localParticipantId = mMeeting?.localParticipant?.id;

      const { senderId, senderName } = data;

      const isLocal = senderId === localParticipantId;

      // Call the toggle function and get the action
      const action = participantRaisedHand(senderId);
      const emoji = action === "lowered" ? "✋" : "🖐🏼";

      // new Audio(
      //     `https://static.videosdk.live/prebuilt/notification.mp3`
      // ).play();

      // toast(
      //   `${
      //     isLocal ? "You" : nameTructed(senderName, 15)
      //   } ${action} hand ${emoji}`,
      //   {
      //     position: "bottom-left",
      //     autoClose: 4000,
      //     hideProgressBar: true,
      //     closeButton: false,
      //     pauseOnHover: true,
      //     draggable: true,
      //     progress: undefined,
      //     theme: "light",
      //   }
      // );
    },
  });

  usePubSub("CHAT", {
    onMessageReceived: (data) => {
      const localParticipantId = mMeeting?.localParticipant?.id;

      const { senderId, senderName, message } = data;

      const isLocal = senderId === localParticipantId;

      // Filter chat messages by breakout room - only show messages from participants in the same room
      if (!isLocal) {
        if (currentRoom) {
          // If in a breakout room, only process messages from participants in the same room
          if (!isInSameRoom(senderId)) {
            logger("log", "🔴 Chat message filtered out - sender not in same breakout room:", senderId);
            return; // Ignore messages from participants in different rooms
          }
        } else {
          // If in main meeting, only process messages from participants NOT in any breakout room
          if (participantRooms[senderId]) {
            logger("log", "🔴 Chat message filtered out - sender is in a breakout room:", senderId);
            return; // Ignore messages from participants in breakout rooms
          }
        }
      }

      if (!isLocal) {
        logger("log", "SIDE BAR MODE CHAT", sideBarMode, sideBarModes.CHAT);
        if (sideBarMode != sideBarModes.CHAT) {
          setUnreadChat(true);
        } else {
          setUnreadChat(false);
        }

        // new Audio(
        //     `https://static.videosdk.live/prebuilt/notification.mp3`
        // ).play();

        // toast(
        //     `${trimSnackBarText(
        //         `${nameTructed(senderName, 15)} says: ${message}`
        //     )}`,
        //     {
        //         position: "bottom-left",
        //         autoClose: 4000,
        //         hideProgressBar: true,
        //         closeButton: false,
        //         pauseOnHover: true,
        //         draggable: true,
        //         progress: undefined,
        //         theme: "light",
        //     }
        // );
      }
    },
  });

  useEffect(() => {
    let isCancelled = false;
    let unsubscribe = null;

    const setupStreamChatListener = async () => {
      try {
        const userId = mMeeting?.localParticipant?.id;
        const userName = mMeeting?.localParticipant?.displayName || "Guest";
        const baseMeetingId = mMeeting?.meetingId;
        if (!userId || !baseMeetingId) return;

        const client = await initGetStreamChat(userId, userName);
        if (!client || isCancelled) return;

        // Determine channel ID based on breakout room status
        let channelId = baseMeetingId;
        if (currentRoom && rooms[currentRoom]) {
          // If in a breakout room, use channel ID with room name and session ID
          const roomName = rooms[currentRoom].name || currentRoom;
          const sanitizedRoomName = roomName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
          channelId = `${baseMeetingId}-${sanitizedRoomName}-${currentRoom}`;
        }

        const channel = client.channel("livestream", channelId);
        try {
          await channel.watch();
        } catch (e) { }
        // try {
        //   await channel.addMembers([userId]);
        // } catch (e) {}

        unsubscribe = channel.on("message.new", (event) => {
          try {
            const msg = event?.message;
            if (!msg) return;

            if (msg.user?.id === userId) return;

            // Filter chat messages by breakout room - only show messages from participants in the same room
            const senderId = msg.user?.id;
            if (currentRoom && senderId) {
              // If in a breakout room, only process messages from participants in the same room
              if (!isInSameRoom(senderId)) {
                logger("log", "🔴 Stream Chat message filtered out - sender not in same breakout room:", senderId);
                return; // Ignore messages from participants in different rooms
              }
            } else if (!currentRoom && senderId) {
              // If in main meeting, only process messages from participants not in any room
              if (participantRooms[senderId]) {
                logger("log", "🔴 Stream Chat message filtered out - sender is in a breakout room:", senderId);
                return; // Ignore messages from participants in breakout rooms
              }
            }

            if (sideBarMode !== sideBarModes.CHAT) {
              setUnreadChat(true);
            }
          } catch (e) { }
        });
      } catch (e) { }
    };

    setupStreamChatListener();

    return () => {
      isCancelled = true;
      try {
        unsubscribe && unsubscribe();
      } catch (e) { }
    };
  }, [
    mMeeting?.localParticipant?.id,
    mMeeting?.meetingId,
    sideBarMode,
    setUnreadChat,
    currentRoom,
    isInSameRoom,
    participantRooms,
    rooms,
  ]);

  usePubSub("MUTE_MIC", {
    onMessageReceived: (data) => {
      logger("log", "MUTE_MIC", data);
      const localParticipantId = mMeeting?.localParticipant?.id;
      const rawMessage = data.message || data;
      const messageData = typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
      const { participantId } = messageData;
      const isMatch = participantId === localParticipantId;
      if (isMatch) {
        mMeeting.toggleMic();
      }
    },
  });

  const { publish: publishCloseElementSection } = usePubSub(
    "CLOSE_ELEMENT_SECTION"
  );

  usePubSub("CLOSE_ELEMENT_SECTION", {
    onMessageReceived: (data) => {
      setActiveElement(null);
    },
  });

  // Main meeting reactions - only process if NOT in a breakout room
  usePubSub("EMOJI_REACTION", {
    onMessageReceived: (data) => {
      logger("log", "EMOJI_REACTION", data);

      // Only process main meeting reactions if we're in the main meeting
      if (currentRoom) {
        logger("log", "🔴 Main meeting reaction ignored - user is in breakout room:", currentRoom);
        return;
      }

      const localParticipantId = mMeeting?.localParticipant?.id;
      const senderId = data.senderId;

      // Ignore reactions from participants in breakout rooms
      if (senderId !== localParticipantId && participantRooms[senderId]) {
        logger("log", "🔴 Reaction filtered out - sender is in a breakout room:", senderId);
        return;
      }

      const rawMessage = data.message || data;
      const messageData = typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
      const { char, alt } = messageData;
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const reaction = {
        id,
        from: data.senderName,
        senderId: senderId,
        emoji: char,
        emojiType: alt,
        ts: Date.now(),
      };
      setRecentReactions((prev) => [...prev.slice(-5), reaction]);
      setTimeout(() => {
        setRecentReactions((prev) => prev.filter((r) => r.id !== id));
      }, 2500);
    },
  });

  // Breakout room reactions - only process if in a breakout room
  usePubSub("EMOJI_REACTION_BREAKOUT", {
    onMessageReceived: (data) => {
      logger("log", "EMOJI_REACTION_BREAKOUT", data);

      // Only process breakout reactions if we're in a breakout room
      if (!currentRoom) {
        logger("log", "🔴 Breakout reaction ignored - user is in main meeting");
        return;
      }

      const localParticipantId = mMeeting?.localParticipant?.id;
      const senderId = data.senderId;

      // Only process reactions from participants in the same breakout room
      if (senderId !== localParticipantId && !isInSameRoom(senderId)) {
        logger("log", "🔴 Breakout reaction filtered out - sender not in same room:", senderId);
        return;
      }

      const rawMessage = data.message || data;
      const messageData = typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
      const { char, alt } = messageData;
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const reaction = {
        id,
        from: data.senderName,
        senderId: senderId,
        emoji: char,
        emojiType: alt,
        ts: Date.now(),
      };
      setRecentReactions((prev) => [...prev.slice(-5), reaction]);
      setTimeout(() => {
        setRecentReactions((prev) => prev.filter((r) => r.id !== id));
      }, 2500);
    },
  });

  // Clear reactions when switching between main meeting and breakout rooms
  useEffect(() => {
    setRecentReactions([]);
  }, [currentRoom, setRecentReactions]);

  // Filter existing reactions when room state changes
  useEffect(() => {
    setRecentReactions((prev) => {
      const filtered = prev.filter((reaction) => {
        const reactionSenderId = reaction.senderId;
        if (!reactionSenderId) return true; // Keep reactions without senderId (legacy)

        // Filter reactions by breakout room
        if (currentRoom) {
          // If in a breakout room, only keep reactions from participants in the same room
          return isInSameRoom(reactionSenderId);
        } else {
          // If in main meeting, only keep reactions from participants NOT in any breakout room
          return !participantRooms[reactionSenderId];
        }
      });
      return filtered;
    });
  }, [currentRoom, participantRooms, isInSameRoom, setRecentReactions]);

  usePubSub("SHOW_ELEMENT_SECTION", {
    onMessageReceived: (data) => {
      logger("log", "SHOW_ELEMENT_SECTION", data);
      const rawMessage = data.message || data;
      const messageData = typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
      setActiveElement(messageData);
    },
  });

  usePubSub("WEBINAR_PARTICIPANT_REQUEST", {
    onMessageReceived: (data) => {
      logger("log", "WEBINAR_PARTICIPANT_REQUEST", data);
      const rawMessage = data.message || data;
      const messageData = typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
      setParticipantWebinarFunc((prevFuncs) => {
        if (!["moderator", "co-host"].includes(meetingRole)) {
          return null;
        }
        const exists = prevFuncs.some(
          (func) => func.participantId === messageData.participantId
        );
        if (!exists) {
          return [
            ...prevFuncs,
            {
              participantId: messageData.participantId,
              name: data.senderName,
            },
          ];
        }
        return prevFuncs;
      });
    },
  });

  usePubSub("WEBINAR_PARTICIPANT_MODE_CHANGE_LISTENER", {
    onMessageReceived: (data) => {
      const rawMessage = data.message || data;
      const messageData = typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;
      const isLocal =
        mMeeting?.localParticipant?.id === messageData.participantId;
      if (isLocal) {
        const { mode, action } = messageData;
        if (action === "approve") {
          mMeeting.changeMode(Constants.modes.SEND_AND_RECV);
        } else if (action === "deny") {
          mMeeting.changeMode(Constants.modes.RECV_ONLY);
          mMeeting.muteMic();
          mMeeting.disableWebcam();
          mMeeting.disableScreenShare();
          toast(`Your request has been denied`, {
            position: "top-right",
            autoClose: 4000,
            hideProgressBar: true,
            closeButton: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
        }
      }
    },
  });

  function onTranslationStateChanged(data) {
    const { status, id } = data;
    logger("log", "onTranslationStateChanged", data);
    // if (status === Constants.translationEvents.TRANSLATION_STARTING) {
    //     logger("log", "Realtime Translation is starting", id);
    // } else if (status === Constants.translationEvents.TRANSLATION_STARTED) {
    //     logger("log", "Realtime Translation is started", id);
    // } else if (status === Constants.translationEvents.TRANSLATION_STOPPING) {
    //     logger("log", "Realtime Translation is stopping", id);
    // } else if (status === Constants.translationEvents.TRANSLATION_STOPPED) {
    //     logger("log", "Realtime Translation is stopped", id);
    // }
  }

  function onTranslationText(data) {
    logger("log", "onTranslationText", data);
    // const { participantId, participantName, text, timestamp, language, type } = data;
    // logger("log", `${participantName} (${language}): ${text} [${timestamp}] - ${type}`);
    logger("log", "onTranslationText", data);
    let { participantId, participantName, text, timestamp, type } = data;
    if (type == "partial") return;
    // Filter out very short or empty transcriptions
    if (!text || text.trim().length < 3) return;

    const cleanedText = text.trim();

    const participant =
      mMeeting.participants?.get?.(participantId) ||
      mMeeting.participants[participantId];
    const profileImage =
      participant?.metaData?.profileImage ||
      participant?.value?.metaData?.profileImage ||
      null;

    setTranscriptions((prev) => {
      // Find the last transcription from this participant
      let lastIndex = -1;
      let lastTranscription = null;

      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].participantId === participantId) {
          lastIndex = i;
          lastTranscription = prev[i];
          break;
        }
      }

      if (lastTranscription) {
        const timeDiff = timestamp - lastTranscription.timestamp;

        // Within 3 seconds from same person - likely same utterance
        if (timeDiff < 3000) {
          // Exact duplicate
          if (lastTranscription.text === cleanedText) {
            return prev; // Skip
          }

          // New text is extension of previous (progressive transcription)
          if (cleanedText.startsWith(lastTranscription.text)) {
            return prev.map((t, index) =>
              index === lastIndex
                ? {
                  ...t,
                  text: cleanedText,
                  timestamp,
                  profileImage: profileImage || t.profileImage,
                }
                : t
            );
          }

          // Previous text is prefix of new text (refined transcription)
          if (
            lastTranscription.text.startsWith(cleanedText) &&
            lastTranscription.text.length > cleanedText.length
          ) {
            return prev; // Keep the longer one
          }

          // Check for high similarity (likely correction/refinement)
          const similarity = calculateSimilarity(
            lastTranscription.text,
            cleanedText
          );
          if (similarity > 0.7) {
            // 70% similar - aggressive deduplication
            // Always replace with the latest version within 3 seconds
            return prev.map((t, index) =>
              index === lastIndex
                ? {
                  ...t,
                  text: cleanedText,
                  timestamp,
                  profileImage: profileImage || t.profileImage,
                }
                : t
            );
          }
        }

        // Between 3-8 seconds - check for very similar content
        if (timeDiff >= 3000 && timeDiff < 8000) {
          // Only merge if very similar or identical
          if (lastTranscription.text === cleanedText) {
            return prev; // Skip exact duplicate
          }

          const similarity = calculateSimilarity(
            lastTranscription.text,
            cleanedText
          );
          if (similarity > 0.9) {
            // Very high similarity
            // Replace with longer version
            if (cleanedText.length > lastTranscription.text.length) {
              return prev.map((t, index) =>
                index === lastIndex
                  ? {
                    ...t,
                    text: cleanedText,
                    timestamp,
                    profileImage: profileImage || t.profileImage,
                  }
                  : t
              );
            }
            return prev; // Keep existing
          }
        }
      }

      // Create new transcription entry
      const transcriptionEntry = {
        id: `${participantId}-${timestamp}`,
        participantId,
        participantName,
        text: cleanedText,
        timestamp,
        type,
        profileImage,
      };

      return [...prev, transcriptionEntry];
    });

    // Show panel when first transcription arrives
    if (!isTranscriptionPanelVisible) {
      setIsTranscriptionPanelVisible(true);
    }
  }
  function onTranslationLanguageChanged(data) {
    const { participantId, language } = data;
    logger("log", "onTranslationLanguageChanged", data);
    logger("log", 
      `Participant ${participantId} changed translation language to ${language}`
    );
  }

  const { startTranslation, stopTranslation, changeTranslationLanguage } =
    useTranslation({
      onTranslationStateChanged,
      onTranslationText,
      onTranslationLanguageChanged,
    });

  // transcription
  const webhookUrl = process.env.REACT_APP_TRANSCRIPTION_WEBHOOK_URL;

  const config = {
    webhookUrl: webhookUrl,
    summary: {
      enabled: true,
      prompt:
        "Write summary in sections like Title, Agenda, Speakers, Action Items, Outlines, Notes and Summary",
    },
  };

  function onTranscriptionStateChanged(data) {
    const { status, id } = data;
    logger("log", "Transcription state changed:", status, id);

    if (status === Constants.transcriptionEvents.TRANSCRIPTION_STARTING) {
      logger("log", "Realtime Transcription is starting", id);
    } else if (status === Constants.transcriptionEvents.TRANSCRIPTION_STARTED) {
      logger("log", "Realtime Transcription is started", id);
    } else if (
      status === Constants.transcriptionEvents.TRANSCRIPTION_STOPPING
    ) {
      logger("log", "Realtime Transcription is stopping", id);
    } else if (status === Constants.transcriptionEvents.TRANSCRIPTION_STOPPED) {
      logger("log", "Realtime Transcription is stopped", id);
    }
  }

  // Callback function for transcription text event with smart deduplication
  function onTranscriptionText(data) {
    let { participantId, participantName, text, timestamp, type } = data;

    // Filter out very short or empty transcriptions
    if (!text || text.trim().length < 3) return;

    const cleanedText = text.trim();

    setTranscriptions((prev) => {
      // Find the last transcription from this participant
      let lastIndex = -1;
      let lastTranscription = null;

      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].participantId === participantId) {
          lastIndex = i;
          lastTranscription = prev[i];
          break;
        }
      }

      if (lastTranscription) {
        const timeDiff = timestamp - lastTranscription.timestamp;

        // Within 3 seconds from same person - likely same utterance
        if (timeDiff < 3000) {
          // Exact duplicate
          if (lastTranscription.text === cleanedText) {
            return prev; // Skip
          }

          // New text is extension of previous (progressive transcription)
          if (cleanedText.startsWith(lastTranscription.text)) {
            return prev.map((t, index) =>
              index === lastIndex ? { ...t, text: cleanedText, timestamp } : t
            );
          }

          // Previous text is prefix of new text (refined transcription)
          if (
            lastTranscription.text.startsWith(cleanedText) &&
            lastTranscription.text.length > cleanedText.length
          ) {
            return prev; // Keep the longer one
          }

          // Check for high similarity (likely correction/refinement)
          const similarity = calculateSimilarity(
            lastTranscription.text,
            cleanedText
          );
          if (similarity > 0.7) {
            // 70% similar - aggressive deduplication
            // Always replace with the latest version within 3 seconds
            return prev.map((t, index) =>
              index === lastIndex ? { ...t, text: cleanedText, timestamp } : t
            );
          }
        }

        // Between 3-8 seconds - check for very similar content
        if (timeDiff >= 3000 && timeDiff < 8000) {
          // Only merge if very similar or identical
          if (lastTranscription.text === cleanedText) {
            return prev; // Skip exact duplicate
          }

          const similarity = calculateSimilarity(
            lastTranscription.text,
            cleanedText
          );
          if (similarity > 0.9) {
            // Very high similarity
            // Replace with longer version
            if (cleanedText.length > lastTranscription.text.length) {
              return prev.map((t, index) =>
                index === lastIndex ? { ...t, text: cleanedText, timestamp } : t
              );
            }
            return prev; // Keep existing
          }
        }
      }

      const participant =
        mMeeting.participants?.get?.(participantId) ||
        mMeeting.participants[participantId];
      const profileImage =
        participant?.metaData?.profileImage ||
        participant?.value?.metaData?.profileImage ||
        null;

      // Create new transcription entry
      const transcriptionEntry = {
        id: `${participantId}-${timestamp}`,
        participantId,
        participantName,
        text: cleanedText,
        timestamp,
        type,
        profileImage,
      };

      return [...prev, transcriptionEntry];
    });

    // Show panel when first transcription arrives
    if (!isTranscriptionPanelVisible) {
      setIsTranscriptionPanelVisible(true);
    }
  }

  // Calculate text similarity (Levenshtein-based)
  function calculateSimilarity(str1, str2) {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;

    if (longer.length === 0) return 1.0;

    const editDistance = getEditDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }

  // Simple edit distance calculation
  function getEditDistance(str1, str2) {
    const costs = [];
    for (let i = 0; i <= str1.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= str2.length; j++) {
        if (i === 0) {
          costs[j] = j;
        } else if (j > 0) {
          let newValue = costs[j - 1];
          if (str1.charAt(i - 1) !== str2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) costs[str2.length] = lastValue;
    }
    return costs[str2.length];
  }

  // Auto-scroll to bottom when new transcriptions arrive
  useEffect(() => {
    if (transcriptionListRef.current) {
      transcriptionListRef.current.scrollTop =
        transcriptionListRef.current.scrollHeight;
    }
  }, [transcriptions]);

  // Hide transcription panel when transcription is turned off
  useEffect(() => {
    if (!transcriptionEnabled) {
      setIsTranscriptionPanelVisible(false);
    }
  }, [transcriptionEnabled]);

  // Adjust panel position on window resize to keep it in bounds
  useEffect(() => {
    const handleResize = () => {
      setPanelPosition((prev) => {
        const maxX = window.innerWidth - panelSize.width;
        const panelHeight = isMobile ? PANEL_MOBILE_HEIGHT : PANEL_FIXED_HEIGHT;
        const maxY = window.innerHeight - panelHeight;
        return {
          x: Math.max(0, Math.min(maxX, prev.x)),
          y: Math.max(0, Math.min(maxY, prev.y)),
        };
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [panelSize.width, PANEL_FIXED_HEIGHT, isMobile, PANEL_MOBILE_HEIGHT]);

  // Handle panel dragging and resizing with limits
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const maxX = window.innerWidth - panelSize.width;
        const panelHeight = isMobile ? PANEL_MOBILE_HEIGHT : PANEL_FIXED_HEIGHT;
        const maxY = window.innerHeight - panelHeight;
        setPanelPosition({
          x: Math.max(0, Math.min(maxX, e.clientX - dragOffset.x)),
          y: Math.max(0, Math.min(maxY, e.clientY - dragOffset.y)),
        });
      }
      if (isResizing) {
        const panel = transcriptionPanelRef.current;
        if (panel) {
          const rect = panel.getBoundingClientRect();
          // Allow resizing until the right edge with a small margin
          const maxWidth = Math.min(
            PANEL_MAX_WIDTH,
            window.innerWidth - rect.left - 20
          );
          const panelHeight = isMobile
            ? PANEL_MOBILE_HEIGHT
            : PANEL_FIXED_HEIGHT;
          setPanelSize({
            width: Math.max(
              PANEL_MIN_WIDTH,
              Math.min(maxWidth, e.clientX - rect.left)
            ),
            height: panelHeight, // Keep height fixed
          });
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    isDragging,
    isResizing,
    dragOffset,
    panelSize.width,
    PANEL_FIXED_HEIGHT,
    isMobile,
    PANEL_MOBILE_HEIGHT,
  ]);

  const handleDragStart = (e) => {
    if (e.target.closest(".cursor-ew-resize")) {
      return;
    }
    const panel = transcriptionPanelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setIsDragging(true);
  };

  const handleResizeStart = (e) => {
    e.stopPropagation();
    setIsResizing(true);
  };

  // Whiteboard drag and resize handlers
  const handleWhiteboardDragStart = (e) => {
    const whiteboard = whiteboardRef.current;
    if (whiteboard && !isWhiteboardResizing) {
      const rect = whiteboard.getBoundingClientRect();
      setWhiteboardDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsWhiteboardDragging(true);
    }
  };

  const handleWhiteboardResizeStart = (e, handle) => {
    e.stopPropagation();
    setWhiteboardResizeHandle(handle);
    setIsWhiteboardResizing(true);
  };

  // Handle whiteboard dragging and resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isWhiteboardDragging) {
        const maxX = window.innerWidth - whiteboardSize.width;
        const maxY = window.innerHeight - whiteboardSize.height;
        setWhiteboardPosition({
          x: Math.max(0, Math.min(maxX, e.clientX - whiteboardDragOffset.x)),
          y: Math.max(0, Math.min(maxY, e.clientY - whiteboardDragOffset.y)),
        });
      }

      if (isWhiteboardResizing && whiteboardResizeHandle) {
        const whiteboard = whiteboardRef.current;
        if (whiteboard) {
          const rect = whiteboard.getBoundingClientRect();
          const minWidth = 400;
          const minHeight = 300;
          const maxWidth = window.innerWidth - rect.left;
          const maxHeight = window.innerHeight - rect.top;

          let newWidth = whiteboardSize.width;
          let newHeight = whiteboardSize.height;

          switch (whiteboardResizeHandle) {
            case "se": // southeast
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, e.clientX - rect.left)
              );
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, e.clientY - rect.top)
              );
              break;
            case "sw": // southwest
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, rect.right - e.clientX)
              );
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, e.clientY - rect.top)
              );
              break;
            case "ne": // northeast
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, e.clientX - rect.left)
              );
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, rect.bottom - e.clientY)
              );
              break;
            case "nw": // northwest
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, rect.right - e.clientX)
              );
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, rect.bottom - e.clientY)
              );
              break;
            case "e": // east
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, e.clientX - rect.left)
              );
              break;
            case "w": // west
              newWidth = Math.max(
                minWidth,
                Math.min(maxWidth, rect.right - e.clientX)
              );
              break;
            case "s": // south
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, e.clientY - rect.top)
              );
              break;
            case "n": // north
              newHeight = Math.max(
                minHeight,
                Math.min(maxHeight, rect.bottom - e.clientY)
              );
              break;
          }

          setWhiteboardSize({ width: newWidth, height: newHeight });

          // Adjust position for handles that resize from left or top
          if (["sw", "nw", "w", "n"].includes(whiteboardResizeHandle)) {
            const widthDiff = newWidth - whiteboardSize.width;
            const heightDiff = newHeight - whiteboardSize.height;

            let newX = whiteboardPosition.x;
            let newY = whiteboardPosition.y;

            if (["sw", "nw", "w"].includes(whiteboardResizeHandle)) {
              newX = whiteboardPosition.x - widthDiff;
            }
            if (["nw", "n"].includes(whiteboardResizeHandle)) {
              newY = whiteboardPosition.y - heightDiff;
            }

            setWhiteboardPosition({
              x: Math.max(0, newX),
              y: Math.max(0, newY),
            });
          }
        }
      }
    };

    const handleMouseUp = () => {
      setIsWhiteboardDragging(false);
      setIsWhiteboardResizing(false);
      setWhiteboardResizeHandle(null);
    };

    if (isWhiteboardDragging || isWhiteboardResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    isWhiteboardDragging,
    isWhiteboardResizing,
    whiteboardDragOffset,
    whiteboardResizeHandle,
    whiteboardSize,
    whiteboardPosition,
  ]);

  // Keep whiteboard within bounds on window resize
  useEffect(() => {
    const handleResize = () => {
      setWhiteboardPosition((prev) => {
        const maxX = window.innerWidth - whiteboardSize.width;
        const maxY = window.innerHeight - whiteboardSize.height;
        return {
          x: Math.max(0, Math.min(maxX, prev.x)),
          y: Math.max(0, Math.min(maxY, prev.y)),
        };
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [whiteboardSize]);

  // Passing callback functions to useTranscription hook
  const { startTranscription, stopTranscription } = useTranscription({
    onTranscriptionStateChanged,
    onTranscriptionText,
  });

  useEffect(() => {
    if (["moderator", "co-host"].includes(meetingRole)) {
      getFolderElementList();
    }
  }, [meetingRole]);

  const getFolderElementList = async () => {
    const response = await getFolderElements(meetingSettings?.roomId);
    logger("log", "response", response.folders);
    if (response.folders) {
      setFolderElementList(response.folders);
    }
  };

  return (
    <>
      {participantsData.length > 0 && (
        <div className="fixed inset-0">
          {["moderator", "co-host"].includes(meetingRole) &&
            approvalSoundEnabled && (
              <audio preload="auto" ref={approvalSoundRef} src={test_sound} />
            )}
          <div ref={containerRef} className="h-full flex flex-col bg-gray-800">
            {showElementSection && (
              <div
                className={`fixed z-50 border border-gray-600 overflow-hidden 
                  left-0 w-full top-16 ${isMobile
                    ? "h-[calc(100vh-4rem-5rem)]"
                    : "h-[calc(100vh-4rem)]"
                  } m-0 bg-gray-800
                   md:left-0 md:w-[30%] md:top-[60px] md:h-[calc(100vh-60px-2rem)] md:rounded-lg`}
              >
                <div className="relative h-full">
                  {/* Close moved inside header in ElementSection on mobile */}
                  <div className="h-full overflow-y-auto">
                    <ElementSection
                      getFolderElementList={getFolderElementList}
                      isRefreshing={isElementRefreshing}
                    />
                  </div>
                </div>
              </div>
            )}
            {activeElement && activeElement.type === "cta" && <CtaSection />}
            {activeElement && activeElement.type === "video" && (
              <VideoSection
                fileUrl={JSON.parse(activeElement.settings).fileUrl}
                onClosePanel={() => {
                  if (meetingRole === "moderator") {
                    publishCloseElementSection({
                      set: true,
                    });
                    setShowElementSection(true);
                  } else {
                    setActiveElement(null);
                  }
                }}
              />
            )}
            {activeElement && activeElement.type === "website" && (
              <WebsiteSection
                settings={JSON.parse(activeElement.settings)}
                onClose={() => {
                  if (meetingRole === "moderator") {
                    publishCloseElementSection({
                      set: true,
                    });
                    setShowElementSection(true);
                  } else {
                    setActiveElement(null);
                  }
                }}
              />
            )}
            {typeof localParticipantAllowedJoin === "boolean" &&
              showJoinScreen == false ? (
              localParticipantAllowedJoin ? (
                <>
                  {/* <BottomBar
                                bottomBarHeight={bottomBarHeight}
                                setIsMeetingLeft={setIsMeetingLeft}
                            /> */}
                  <TopBarWebinar
                    setIsMeetingLeft={setIsMeetingLeft}
                    setCustomVideoStream={setCustomVideoStream}
                    micOn={micOn}
                    setMicOn={setMicOn}
                    webcamOn={webcamOn}
                    setWebcamOn={setWebcamOn}
                    customAudioStream={customAudioStream}
                    setCustomAudioStream={setCustomAudioStream}
                    customVideoStream={customVideoStream}
                    startTranscription={() => startTranscription(config)}
                    stopTranscription={stopTranscription}
                    startTranslation={startTranslation}
                    stopTranslation={stopTranslation}
                    changeTranslationLanguage={changeTranslationLanguage}
                  />
                  <div
                    className={`flex flex-1 flex-row bg-gray-800 ${isMobileLike ? "pb-24" : ""
                      } ${showElementSection ? "ml-[22rem]" : ""
                      } transition-all duration-300`}
                    style={{
                      ...(meetingSettings?.meetingBackgroundImage && {
                        backgroundImage: `url(${meetingSettings.meetingBackgroundImage})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                      }),
                      ...(meetingSettings?.meetingBackgroundGradient && {
                        backgroundImage: `linear-gradient(${meetingSettings.meetingBackgroundGradient.gradientDirection}, ${meetingSettings.meetingBackgroundGradient.gradientColor1}, ${meetingSettings.meetingBackgroundGradient.gradientColor2})`,
                      }),
                      ...(!meetingSettings?.meetingBackgroundImage &&
                        !meetingSettings?.meetingBackgroundGradient && {
                        backgroundColor:
                          meetingSettings?.meetingBackgroundSolid ||
                          "#050a0e",
                      }),
                    }}
                  >
                    <div
                      className={`flex flex-1 ${effectiveIsPresenting ? "flex-col" : ""
                        }`}
                    >
                      {effectiveIsPresenting ? (
                        <PresenterView
                          height={
                            isMobileLike
                              ? Math.max(0, containerHeight - 96)
                              : Math.max(
                                0,
                                containerHeight - bottomBarHeight - 220
                              )
                          }
                        // height={540}
                        />
                      ) : null}
                      {effectiveIsPresenting && isMobile ? (
                        participantsData.map((participantId) => (
                          <ParticipantMicStream
                            key={participantId}
                            participantId={participantId}
                          />
                        ))
                      ) : (
                        <MemorizedWebinarParticipantView
                          isPresenting={isPresenting}
                        />
                      )}
                    </div>

                    <SidebarConatiner
                      height={containerHeight - bottomBarHeight}
                      sideBarContainerWidth={sideBarContainerWidth}
                    />
                  </div>
                  {whiteboardUrl && (
                    <div
                      ref={whiteboardRef}
                      className={`fixed z-40 bg-white shadow-2xl overflow-hidden border-2 ${isMobile ? "left-4 right-4 top-4" : ""
                        }`}
                      style={{
                        ...(isMobile
                          ? {
                            bottom: `${bottomBarHeight + 16}px`,
                            cursor: "default",
                            borderColor: "#928D9B",
                            borderRadius: "10px",
                            boxShadow:
                              "0 10px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(146, 141, 155, 0.2)",
                          }
                          : {
                            left: `${whiteboardPosition.x}px`,
                            top: `${whiteboardPosition.y}px`,
                            width: `${whiteboardSize.width}px`,
                            height: `${whiteboardSize.height}px`,
                            cursor: isWhiteboardDragging
                              ? "grabbing"
                              : "default",
                            borderColor: "#928D9B",
                            borderRadius: "10px",
                            boxShadow:
                              "0 10px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(146, 141, 155, 0.2)",
                          }),
                      }}
                    >
                      {/* Draggable Header */}
                      <div
                        className="absolute top-0 left-0 right-0 h-10 flex items-center justify-between px-3 z-20"
                        onMouseDown={
                          !isMobile ? handleWhiteboardDragStart : undefined
                        }
                        style={{
                          ...(!isMobile
                            ? { cursor: "grab" }
                            : { cursor: "default" }),
                          userSelect: "none",
                          backgroundColor: "#030325",
                          borderTopLeftRadius: "10px",
                          borderTopRightRadius: "10px",
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <svg
                            className="w-4 h-4 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            />
                          </svg>
                          <span className="text-white text-sm font-medium">
                            Whiteboard
                          </span>
                        </div>
                        {meetingRole === "moderator" && (
                          <button
                            onClick={() => stopWhiteboard()}
                            className="bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center transition-colors"
                            title="Close Whiteboard"
                          >
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        )}
                      </div>

                      {/* Whiteboard Content */}
                      <div className="w-full h-full pt-10">
                        <iframe
                          src={whiteboardUrl}
                          className={`w-full h-full border-0 ${meetingRole === "moderator"
                            ? "pointer-events-auto"
                            : "pointer-events-none"
                            }`}
                          title="Whiteboard"
                        />
                      </div>

                      {/* Resize Handles */}
                      {meetingRole === "moderator" && !isMobile && (
                        <>
                          {/* Corner handles */}
                          <div
                            className="absolute w-3 h-3 bg-blue-500 hover:bg-blue-600 cursor-nw-resize opacity-70 hover:opacity-100 transition-opacity"
                            style={{
                              top: "-2px",
                              left: "-2px",
                              borderRadius: "50% 0 0 0",
                            }}
                            onMouseDown={(e) =>
                              handleWhiteboardResizeStart(e, "nw")
                            }
                            title="Resize from top-left"
                          />
                          <div
                            className="absolute w-3 h-3 bg-blue-500 hover:bg-blue-600 cursor-ne-resize opacity-70 hover:opacity-100 transition-opacity"
                            style={{
                              top: "-2px",
                              right: "-2px",
                              borderRadius: "0 50% 0 0",
                            }}
                            onMouseDown={(e) =>
                              handleWhiteboardResizeStart(e, "ne")
                            }
                            title="Resize from top-right"
                          />
                          <div
                            className="absolute w-3 h-3 bg-blue-500 hover:bg-blue-600 cursor-sw-resize opacity-70 hover:opacity-100 transition-opacity"
                            style={{
                              bottom: "-2px",
                              left: "-2px",
                              borderRadius: "0 0 0 50%",
                            }}
                            onMouseDown={(e) =>
                              handleWhiteboardResizeStart(e, "sw")
                            }
                            title="Resize from bottom-left"
                          />
                          <div
                            className="absolute w-3 h-3 bg-blue-500 hover:bg-blue-600 cursor-se-resize opacity-70 hover:opacity-100 transition-opacity"
                            style={{
                              bottom: "-2px",
                              right: "-2px",
                              borderRadius: "0 0 50% 0",
                            }}
                            onMouseDown={(e) =>
                              handleWhiteboardResizeStart(e, "se")
                            }
                            title="Resize from bottom-right"
                          />

                          {/* Edge handles */}
                          <div
                            className="absolute w-1 h-full bg-blue-400 hover:bg-blue-500 cursor-ew-resize opacity-50 hover:opacity-80 transition-opacity"
                            style={{ left: "-2px", top: "0" }}
                            onMouseDown={(e) =>
                              handleWhiteboardResizeStart(e, "w")
                            }
                            title="Resize width from left"
                          />
                          <div
                            className="absolute w-1 h-full bg-blue-400 hover:bg-blue-500 cursor-ew-resize opacity-50 hover:opacity-80 transition-opacity"
                            style={{ right: "-2px", top: "0" }}
                            onMouseDown={(e) =>
                              handleWhiteboardResizeStart(e, "e")
                            }
                            title="Resize width from right"
                          />
                          <div
                            className="absolute w-full h-1 bg-blue-400 hover:bg-blue-500 cursor-ns-resize opacity-50 hover:opacity-80 transition-opacity"
                            style={{ top: "-2px", left: "0" }}
                            onMouseDown={(e) =>
                              handleWhiteboardResizeStart(e, "n")
                            }
                            title="Resize height from top"
                          />
                          <div
                            className="absolute w-full h-1 bg-blue-400 hover:bg-blue-500 cursor-ns-resize opacity-50 hover:opacity-80 transition-opacity"
                            style={{ bottom: "-2px", left: "0" }}
                            onMouseDown={(e) =>
                              handleWhiteboardResizeStart(e, "s")
                            }
                            title="Resize height from bottom"
                          />
                        </>
                      )}
                    </div>
                  )}

                  {/* Transcription Panel - Transparent Style */}
                  {transcriptionEnabled && isTranscriptionPanelVisible && (
                    <div
                      ref={transcriptionPanelRef}
                      className="fixed z-50 flex flex-col"
                      style={{
                        ...(isMobile
                          ? {
                            left: "50%",
                            transform: "translateX(-50%)",
                            bottom: "20px",
                            top: "auto",
                            width: `${Math.min(
                              panelSize.width,
                              window.innerWidth - 32
                            )}px`,
                          }
                          : {
                            left: `${panelPosition.x}px`,
                            top: `${panelPosition.y}px`,
                            width: `${panelSize.width}px`,
                          }),
                        height: `${isMobile ? PANEL_MOBILE_HEIGHT : panelSize.height
                          }px`,
                        cursor: isDragging ? "grabbing" : "grab",
                        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.5)",
                        borderRadius: "8px",
                        backgroundColor: "rgba(0, 0, 0, 0.35)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255, 255, 255, 0.2)",
                        userSelect: "none",
                      }}
                      onMouseDown={handleDragStart}
                    >
                      {/* Transcription List - Transparent Style */}
                      <div
                        ref={transcriptionListRef}
                        className="flex-1 overflow-y-auto"
                        style={{
                          overflowY: "auto",
                          backgroundColor: "transparent",
                          borderRadius: "8px",
                          height: `${isMobile ? PANEL_MOBILE_HEIGHT : panelSize.height
                            }px`,
                        }}
                      >
                        {transcriptions.length === 0 ? (
                          <div className="flex items-center justify-center h-full">
                            <div className="text-center py-8">
                              <svg
                                className="w-16 h-16 mx-auto mb-3 text-white opacity-50"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                style={{
                                  filter:
                                    "drop-shadow(0 1px 2px rgba(0,0,0,0.5))",
                                }}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={1.5}
                                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                                />
                              </svg>
                              <p
                                className="text-sm font-medium text-white"
                                style={{
                                  textShadow: "0 1px 3px rgba(0,0,0,0.8)",
                                }}
                              >
                                No captions yet
                              </p>
                              <p
                                className="text-xs text-white opacity-70 mt-1"
                                style={{
                                  textShadow: "0 1px 2px rgba(0,0,0,0.8)",
                                }}
                              >
                                Transcriptions will appear here
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="px-3 py-2">
                            {transcriptions.map((transcription, index) => (
                              <div
                                key={transcription.id || index}
                                className="py-2.5 border-b last:border-b-0"
                                style={{
                                  borderColor: "rgba(255, 255, 255, 0.1)",
                                }}
                              >
                                <div className="flex items-start gap-2.5">
                                  <div
                                    className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center overflow-hidden text-white text-xs font-semibold mt-0.5"
                                    style={{
                                      backgroundColor:
                                        transcription.profileImage ||
                                          mMeeting.participants?.get?.(
                                            transcription.participantId
                                          )?.metaData?.profileImage ||
                                          mMeeting.participants?.[
                                            transcription.participantId
                                          ]?.value?.metaData?.profileImage
                                          ? "transparent"
                                          : "rgba(45, 140, 255, 0.8)",
                                    }}
                                  >
                                    {transcription.profileImage ||
                                      mMeeting.participants?.get?.(
                                        transcription.participantId
                                      )?.metaData?.profileImage ||
                                      mMeeting.participants?.[
                                        transcription.participantId
                                      ]?.value?.metaData?.profileImage ? (
                                      <img
                                        src={
                                          transcription.profileImage ||
                                          mMeeting.participants?.get?.(
                                            transcription.participantId
                                          )?.metaData?.profileImage ||
                                          mMeeting.participants?.[
                                            transcription.participantId
                                          ]?.value?.metaData?.profileImage
                                        }
                                        alt={
                                          transcription.participantName ||
                                          "Participant"
                                        }
                                        className="w-full h-full object-cover rounded-full"
                                      />
                                    ) : (
                                      transcription.participantName
                                        ?.charAt(0)
                                        .toUpperCase()
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline gap-2 mb-0.5">
                                      <span
                                        className="font-semibold text-xs text-white"
                                        style={{
                                          textShadow:
                                            "0 1px 3px rgba(0,0,0,0.9)",
                                        }}
                                      >
                                        {transcription.participantName}
                                      </span>
                                      <span
                                        className="text-xs text-white opacity-60"
                                        style={{
                                          textShadow:
                                            "0 1px 2px rgba(0,0,0,0.9)",
                                        }}
                                      >
                                        {new Date(
                                          transcription.timestamp
                                        ).toLocaleTimeString([], {
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })}
                                      </span>
                                    </div>
                                    <p
                                      className="text-sm text-white leading-relaxed break-words"
                                      style={{
                                        textShadow: "0 1px 3px rgba(0,0,0,0.9)",
                                        textAlign: "left",
                                      }}
                                    >
                                      {transcription.text}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Right Edge Resize Handle - Visual Indicator */}
                      <div
                        className="absolute right-0 top-0 bottom-0 w-3 cursor-ew-resize group"
                        onMouseDown={handleResizeStart}
                        title="Drag to resize width"
                        style={{
                          borderTopRightRadius: "8px",
                          borderBottomRightRadius: "8px",
                        }}
                      >
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-white/20" />
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <></>
              )
            ) : (
              <>
                {showJoinScreen && <WaitingToJoinScreenNew />}
                {showWaitingApproval && <WaitingForApproval />}
              </>
            )}
          </div>
        </div>
      )}
      {participantsData.length === 0 && <WebinerWaiting />}
    </>
  );
}
